import { supabase, type ReminderEvent } from '@ubus/shared';

export type DriverReminderInboxRow = ReminderEvent;

export async function fetchDriverReminderInbox(
  driverId: string
): Promise<{ rows: DriverReminderInboxRow[]; unreadIds: string[] }> {
  const { data: deliveries, error } = await supabase
    .from('reminder_deliveries')
    .select('event_id, status')
    .eq('channel', 'in_app_driver')
    .eq('recipient', driverId)
    .in('status', ['sent', 'pending']);
  if (error) throw error;

  const eventIds = [...new Set((deliveries || []).map((d) => d.event_id))];
  if (eventIds.length === 0) return { rows: [], unreadIds: [] };

  const [{ data: events, error: evErr }, { data: reads, error: readErr }] = await Promise.all([
    supabase
      .from('reminder_events')
      .select('*')
      .in('id', eventIds)
      .eq('status', 'open')
      .eq('subject_kind', 'driver')
      .eq('subject_id', driverId)
      .order('created_at', { ascending: false })
      .limit(100),
    supabase
      .from('reminder_reads')
      .select('event_id')
      .eq('reader_kind', 'driver')
      .eq('reader_id', driverId)
      .in('event_id', eventIds),
  ]);
  if (evErr) throw evErr;
  if (readErr) throw readErr;

  const readSet = new Set((reads || []).map((r) => r.event_id));
  const rows = (events || []) as DriverReminderInboxRow[];
  const unreadIds = rows.filter((r) => !readSet.has(r.id)).map((r) => r.id);
  return { rows, unreadIds };
}

export async function markDriverReminderRead(
  driverId: string,
  eventIds: string[]
): Promise<void> {
  if (eventIds.length === 0) return;
  const now = new Date().toISOString();
  let readerLabel = driverId.slice(0, 8);
  const { data: driver } = await supabase
    .from('drivers')
    .select('name')
    .eq('id', driverId)
    .maybeSingle();
  if (driver?.name?.trim()) readerLabel = driver.name.trim();

  const payload = eventIds.map((event_id) => ({
    event_id,
    reader_kind: 'driver' as const,
    reader_id: driverId,
    reader_label: readerLabel,
    read_at: now,
  }));
  const { error } = await supabase
    .from('reminder_reads')
    .upsert(payload, { onConflict: 'event_id,reader_kind,reader_id' });
  if (error) throw error;
}
